<template>
  <v-responsive
    class="v-img bg-gray/20 overflow-hidden relative"
    :class="[rounded ? 'rounded-circle' : 'rounded-lg']"
    :aria-label="normalisedSrc.alt"
    :role="normalisedSrc.alt ? 'img' : undefined"
    :aspect-ratio="normalisedSrc.aspectRatio"
    :style="{ width: w }"
  >
    <template v-if="normalisedSrc.src">
      <v-img
        v-if="blurBg"
        :src="lazySrc || normalisedSrc.src"
        class="image-bg"
        width="100%"
        height="100%"
        cover
      />
      <NuxtImg
        v-if="normalisedSrc.src"
        class="v-img__img image-content__img"
        :class="containClasses"
        :placeholder="lazySrc"
        :src="normalisedSrc.src"
        v-bind="normalisedSrc.modifiers"
        :provider="provider"
      />
    </template>
  </v-responsive>
</template>

<script lang="ts">
import { isObject, isString } from "lodash";
import { defineComponent, type PropType } from "vue";
import type { PublicMedia } from "@orie/sdk";

export const isPublicMedia = (
  src: PublicMedia | string | undefined
): src is PublicMedia => {
  return !!src && !isString(src);
};

export default defineComponent({
  name: "ImageField",
  props: {
    aspectRatio: { type: [String, Number] },
    src: { type: Object as PropType<PublicMedia | string> },
    srcset: { type: String },
    lazySrc: { type: String },
    size: { type: [String, Number], default: "40" },
    width: { type: [String, Number] },
    height: { type: [String, Number] },
    alt: { type: String },
    fit: { type: String as PropType<"cover" | "contain">, default: "contain" },
    rounded: { type: Boolean, default: false },
    blurBg: { type: Boolean, default: false },
    modifiers: { type: Object },
  },
  setup(props) {
    const normalisedSrc = computed(() => {
      if (isPublicMedia(props.src)) {
        const width = props.width || props.src.width || props.size;
        const height = props.height || props.src.height;

        return {
          // src: props.src.fqfn,
          width,
          height,
          src: props.src.src,
          srcset: props.srcset || props.src.srcset,
          lazySrc: props.lazySrc || props.src.thumb,
          aspectRatio: Number(props.aspectRatio || props.src.aspectRatio || 0),
          alt: props.alt || props.src.alt,
          modifiers: {
            width,
            height,
            fit: props.fit,
            ...omitNil(props.modifiers),
          },
        };
      }

      return {
        src: props.src,
        srcset: props.srcset,
        lazySrc: props.lazySrc,
        aspectRatio: Number(props.aspectRatio || 0),
        width: props.width || props.size,
        height: props.height,
        alt: props.alt,
      };
    });

    const containClasses = computed(() => ({
      "v-img__img--cover": props.fit === "cover",
      "v-img__img--contain": props.fit !== "cover",
    }));

    const w = computed(() => convertToUnit(normalisedSrc.value.width));

    return {
      normalisedSrc,
      containClasses,
      w,
    };
  },
  computed: {
    provider() {
      if (isObject(this.src) && this.src.fqfn) {
        return "orie";
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.image-bg {
  @apply absolute inset-0 z-[-10];
  filter: blur(7px);
}
</style>
